/**
 * How a receipt reading meets the expense form. High-confidence values fill only fields the user
 * has not set; everything else (low confidence, the payment method, values that differ from what
 * the user typed) is offered as a suggestion the user can apply with one tap.
 */
import type { ReceiptSuggestion } from "../merchant-recognition";
import type { DraftForm } from "../model";
import type { ReceiptReading } from "./receipt-parser";

export type ExtractedField = "amount" | "purchaseDate" | "merchant" | "categoryId" | "paymentMethodId";

/** Form values as typed in the form: the amount uses a decimal comma. */
const formAmount = (decimal: string) => decimal.replace(".", ",");

/**
 * @param defaults the values a new form starts with: a date still equal to the default counts as
 *   "not set by the user" (a photo taken today of yesterday's receipt).
 */
export function mergeExtraction(
  form: DraftForm,
  reading: ReceiptReading | null,
  suggestion: ReceiptSuggestion | null,
  defaults: { purchaseDate: string },
): { form: DraftForm; applied: Partial<Record<ExtractedField, string>> } {
  const next = { ...form };
  const applied: Partial<Record<ExtractedField, string>> = {};
  if (reading?.amount?.confidence === "high" && form.amount.trim() === "") {
    next.amount = applied.amount = formAmount(reading.amount.value);
  }
  if (reading?.date?.confidence === "high" && (form.purchaseDate === "" || form.purchaseDate === defaults.purchaseDate) && reading.date.value !== form.purchaseDate) {
    next.purchaseDate = applied.purchaseDate = reading.date.value;
  }
  if (suggestion?.merchant?.confidence === "high" && form.merchant.trim() === "") {
    next.merchant = applied.merchant = suggestion.merchant.value;
  }
  if (suggestion?.category && form.categoryId === "") {
    next.categoryId = applied.categoryId = suggestion.category.categoryId;
  }
  // The payment method decides the accounting month: never filled automatically.
  return { form: next, applied };
}

export interface FieldSuggestion {
  field: ExtractedField;
  /** The value in form format. */
  value: string;
  confidence: "high" | "low";
}

/** Read values that differ from what the form holds now (to offer as "Usa" chips). */
export function pendingSuggestions(form: DraftForm, reading: ReceiptReading | null, suggestion: ReceiptSuggestion | null): FieldSuggestion[] {
  const out: FieldSuggestion[] = [];
  const offer = (field: ExtractedField, value: string | undefined, confidence: "high" | "low" | undefined) => {
    if (value === undefined || confidence === undefined) return;
    const current = field === "amount" ? form.amount.trim().replace(".", ",") : String(form[field] ?? "");
    const same = field === "merchant" ? current.toLowerCase() === value.toLowerCase() : current === value;
    if (!same) out.push({ field, value, confidence });
  };
  offer("amount", reading?.amount ? formAmount(reading.amount.value) : undefined, reading?.amount?.confidence);
  offer("purchaseDate", reading?.date?.value, reading?.date?.confidence);
  offer("merchant", suggestion?.merchant?.value, suggestion?.merchant?.confidence);
  offer("categoryId", suggestion?.category?.categoryId, suggestion?.category ? "high" : undefined);
  offer("paymentMethodId", suggestion?.paymentMethod?.paymentMethodId, suggestion?.paymentMethod?.confidence);
  return out;
}
